import { useCallback, useEffect, useRef, useState } from 'react';

type AutoReturnToFalseStateHook = (args: {
  falsingDelay: number;
  startWithTrue?: boolean;
}) => [boolean, () => void];

// Each time the value is set to true, wait for some seconds before setting it back to false
const useAutoReturnToFalseState: AutoReturnToFalseStateHook = ({
  falsingDelay,
  startWithTrue = false,
}) => {
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const isFirstMount = useRef(true);
  const [value, setValue] = useState(false);

  const setToTrue = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    setValue(true);
    timeoutIdRef.current = setTimeout(() => {
      setValue(false);
    }, falsingDelay);
  }, [falsingDelay]);

  useEffect(() => {
    if (isFirstMount.current) {
      if (startWithTrue) {
        setToTrue();
      }
      isFirstMount.current = false;
    }

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [startWithTrue, setToTrue]);

  return [value, setToTrue];
};

export default useAutoReturnToFalseState;
